import { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { getToken } from "../../lib/tokenStorage";
import { Youtube, FileText, Download } from "lucide-react";

function ytEmbedId(url) {
  const m = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}
function thumb(url) { const id = ytEmbedId(url); return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null; }

const fmtSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Merged pre-arrival feed — pulls the legacy `/prearrival-videos` (YouTube-only)
 * AND the newer `/pre-arrival-docs` (files + YouTube with partner scoping) so
 * partners see EVERYTHING Founder/BDOM added, regardless of which form they
 * used. Files open via authenticated blob download.
 */
export default function PrearrivalTab() {
  const [items, setItems] = useState([]);

  const openDoc = async (docId, filename) => {
    // Popup-blocker workaround — browsers block `window.open()` called
    // after an awaited fetch because the user gesture is lost. Open the
    // tab SYNCHRONOUSLY here (on the raw click) with a placeholder, then
    // swap its location to the blob URL once the download resolves. If
    // the popup was blocked anyway (strict browsers, in-app webviews),
    // fall back to a programmatic `<a download>` click so the user
    // still gets the file.
    const win = window.open("", "_blank", "noopener");
    if (win) {
      // Safe DOM API instead of document.write (flagged as an XSS sink
      // by the code reviewer, though the string here is static and not
      // user-supplied). createElement + textContent is XSS-proof.
      try {
        win.document.title = "Loading…";
        const div = win.document.createElement("div");
        div.textContent = "Fetching document…";
        div.style.cssText = "font:14px system-ui;color:#555;padding:24px";
        win.document.body.appendChild(div);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("PrearrivalTab: could not paint loading placeholder", e);
        }
      }
    }
    try {
      const resp = await fetch(`${API}/pre-arrival-docs/${docId}/file`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      if (win && !win.closed) {
        // Popup opened — point it at the blob so the browser renders it
        // inline (PDF viewer / image viewer / etc.) and the user can hit
        // the browser's own download button.
        win.location.href = url;
      } else {
        // Popup blocked — programmatic download so the user still ends
        // up with the file locally.
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "document";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Give the target window ~60s to load before revoking the URL.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      api.post(`/pre-arrival-docs/${docId}/view`).catch(() => {});
    } catch (e) {
      if (win && !win.closed) win.close();
      if (process.env.NODE_ENV !== "production") {
        console.warn("PrearrivalTab: preview open failed", e);
      }
    }
  };

  // Single mount-time fetch — no external state read, so an empty
  // dep array is intentional. React setters (setItems) are stable.
  useEffect(() => {
    Promise.all([
      api.get("/prearrival-videos").then(r => r.data || []).catch(() => []),
      api.get("/pre-arrival-docs").then(r => r.data || []).catch(() => []),
    ]).then(([legacy, docs]) => {
      const merged = [
        ...docs.map(d => ({
          id: d.id,
          title: d.title,
          description: d.filename || "",
          kind: d.kind || (d.file_id ? "file" : "youtube"),
          youtube_url: d.youtube_url || "",
          file_id: d.file_id || null,
          filename: d.filename || "",
          size: d.size || 0,
          created_at: d.created_at || "",
        })),
        ...legacy.map(v => ({
          id: v.id,
          title: v.title,
          description: v.description || "",
          kind: "youtube",
          youtube_url: v.youtube_url || "",
          file_id: null,
          size: 0,
          created_at: v.created_at || "",
        })),
      ];
      merged.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setItems(merged);
    });
  }, []);

  return (
    <div className="space-y-3" data-testid="prearrival-tab">
      <p className="text-sm text-[color:var(--aii-text-2)]">Pre-arrival training materials from your BDOM — share with your candidates before departure.</p>
      {items.length === 0 && (
        <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]" data-testid="pa-empty">
          Nothing here yet. Your BDOM will add training videos and documents you can share with departing candidates.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(v => v.kind === "file" ? (
          <button key={v.id} onClick={() => openDoc(v.id, v.filename)} data-testid={`pa-file-${v.id}`}
                  className="aii-card p-4 text-left hover:border-brand hover:shadow-md transition group">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[color:var(--aii-green-tint)] flex items-center justify-center flex-shrink-0">
                <FileText size={18} className="text-brand"/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-[color:var(--aii-text-1)] truncate">{v.title}</div>
                <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5 truncate">{v.filename || "Document"}</div>
                <div className="text-[10px] text-brand mt-1 flex items-center gap-1 font-semibold"><Download size={9}/> {fmtSize(v.size)} · Tap to open in new tab & download</div>
              </div>
            </div>
          </button>
        ) : (
          <a key={v.id} href={v.youtube_url} target="_blank" rel="noopener noreferrer"
             onClick={() => api.post(`/pre-arrival-docs/${v.id}/view`).catch(() => {})}
             data-testid={`pa-video-${v.id}`}
             className="aii-card overflow-hidden hover:shadow-md transition">
            <div className="aspect-video bg-slate-900 relative">
              {thumb(v.youtube_url) ? (
                <img src={thumb(v.youtube_url)} alt="" className="w-full h-full object-cover"/>
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Youtube size={32} className="text-white/60"/></div>
              )}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 bg-black/60 rounded-full flex items-center justify-center">
                  <Youtube size={24} className="text-white"/>
                </div>
              </div>
            </div>
            <div className="p-3">
              <div className="font-semibold text-sm">{v.title}</div>
              {v.description && <div className="text-xs text-[color:var(--aii-muted)] mt-0.5 line-clamp-2">{v.description}</div>}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
